<template>
  <v-dialog
      max-width="780px"
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="onenter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Smart Offer ({{ this.rules.name }})</span>
        <v-col cols="4" sm="2" md="2">
          <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
        </v-col>
        <v-spacer/>
        <r-authorize
            :master="masterAccess"
            :locked.sync="model.locked"
            :enabled.sync="model.enabled"
        />
      </v-card-title>

      <v-card-text class="resizable-card__body">
        <v-tabs v-model="tab" left background-color="blue-grey lighten-5">
          <v-tab key="tab_general">GENERAL</v-tab>
          <v-tab key="tab_bundles">BUNDLES</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <v-tab-item key="tab_general">
            <v-col cols="12">
              <v-row dense class="mt-2">
                <v-col cols="12" md="9" sm="9">
                  <v-text-field
                      disabled
                      label="UID:"
                      v-model.trim="model.uid"
                      hide-details
                      outlined
                      dense
                  />
                </v-col>
                <v-col cols="12" sm="3" md="3">
                  <v-switch
                      class="mt-1"
                      v-model="model.remoteAssets"
                      label="Remote Assets"
                      color="success"
                      hide-details
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" md="4" sm="4">
                  <v-text-field
                      label="Name:"
                      v-model.trim="model.name"
                      v-ascii-only
                  />
                </v-col>
                <v-col cols="12" md="4" sm="4">
                  <v-text-field
                      label="Key:"
                      v-model.trim="model.key"
                      v-ascii-only
                  />
                </v-col>
                <v-col cols="12" sm="4" md="4">
                  <v-autocomplete
                      v-model="model.showOnLaunch"
                      :items="itemsShowOnLaunch"
                      item-text="name"
                      item-value="value"
                      label="Show on launch"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" md="4" sm="4">
                  <v-text-field
                      label="Patch key:"
                      v-model.trim="model.patchKey"
                      v-ascii-only
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" md="3" sm="3">
                  <r-smart-offer-kind :model.sync="model.kind" label="Group"/>
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-text-field
                      v-model.number="model.delayShow"
                      label="Delay Show"
                      hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-text-field
                      v-model.number="model.quantity"
                      label="Quantity"
                      hide-details
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" sm="3" md="3">
                  <r-smart-offer-button-place
                      :model.sync="model.buttonPlace"
                      label="Button Place"
                  />
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-text-field
                      v-model.number="model.buttonPriority"
                      label="Button Priority"
                      hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-text-field
                      v-model.number="model.buttonTimeout"
                      label="Button Timeout"
                      hide-details
                  />
                </v-col>
              </v-row>

              <v-row dense class="mb-4">
                <v-col cols="12" sm="2" md="2">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                          v-model.number="model.launchDelay"
                          v-on="on"
                          v-bind="attrs"
                          label="Launch Delay"
                          hide-details
                      />
                    </template>
                    <div>Delay after the map loads before the offer becomes available.</div>
                  </v-tooltip>
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                          v-model.number="model.duration"
                          v-on="on"
                          v-bind="attrs"
                          label="Duration"
                          hide-details
                      />
                    </template>
                    <dl>
                      <dt> n: DEFAULT</dt>
                      <dt> 0: ONE_TIME</dt>
                      <dt>-1: UNLIMITED</dt>
                    </dl>
                  </v-tooltip>
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-text-field
                      v-model.number="model.cooldown"
                      label="Cooldown"
                      hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-text-field
                      v-model.number="model.cooldownPassed"
                      label="Cooldown Passed"
                      hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-text-field
                      v-model.number="model.resetTime"
                      label="Reset Time"
                      hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-text-field
                      v-model.number="model.lastwave"
                      label="Lastwave"
                      hide-details
                  />
                </v-col>
              </v-row>

              <v-expansion-panels focusable>
                <r-panel title="Advanced settings">
                  <v-row dense class="mt-2 mb-4">
                    <v-col cols="12" sm="3" md="3">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                              v-model.number="model.launchDelayOffset"
                              v-on="on"
                              v-bind="attrs"
                              label="Launch Delay Offset"
                              hide-details
                          />
                        </template>
                        <div>A random value in the range [0..n] is added to the LaunchDelay parameter</div>
                      </v-tooltip>
                    </v-col>
                    <v-col cols="12" sm="3" md="3">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                              v-model.number="model.cooldownOffset"
                              v-on="on"
                              v-bind="attrs"
                              label="Cooldown Offset"
                              hide-details
                          />
                        </template>
                        <div>A random value in the range [0..n] is added to the Cooldown parameter</div>
                      </v-tooltip>
                    </v-col>
                    <v-col cols="12" sm="3" md="3">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                              v-model.number="model.cooldownPassedOffset"
                              v-on="on"
                              v-bind="attrs"
                              label="Cooldown Passed Offset"
                              hide-details
                          />
                        </template>
                        <div>A random value in the range [0..n] is added to the CooldownPassed parameter</div>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                  <v-row dense class="mb-2">
                    <v-col cols="12" sm="3" md="3">
                      <v-text-field
                          v-model.number="model.extendDuration"
                          label="Extend Duration"
                          hide-details
                      />
                    </v-col>
                    <v-col cols="12" sm="3" md="3">
                      <v-text-field
                          v-model.number="model.extendPending"
                          label="Extend Pending"
                          hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3" sm="3">
                      <v-text-field
                          label="AutoOpen Cooldown"
                          v-model.number="model.autoOpenCooldown"
                          hide-details
                      />
                    </v-col>
                  </v-row>
                  <v-row dense class="mb-2">
                    <v-col cols="12" sm="6" md="6">
                      <r-talk-dropdown
                          :model.sync="model.talkActive"
                          :clearable="true"
                          label="Talk Active"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="6">
                      <r-talk-dropdown
                          :model.sync="model.talkClosed"
                          :clearable="true"
                          label="Talk Closed"
                      />
                    </v-col>
                  </v-row>
                </r-panel>
                <r-guard-time-panel
                    :model.sync="model.guardTime"
                    title="Guard Time"
                />
                <r-reminder-panel
                    :model.sync="model.reminder"
                    title="Reminders"
                />
                <r-smart-offer-skin-panel
                    :items.sync="model.skins"
                    title="Skins"
                />
                <r-trigger-panel
                    :items.sync="model.triggersAvailable"
                    title="Triggers - Available"
                />
                <r-trigger-panel
                    :items.sync="model.triggersComplete"
                    title="Triggers - Complete"
                />
              </v-expansion-panels>
              <v-expansion-panels>
                <r-panel title="Map Group">
                  <r-map-group-dropdown :model.sync="model.mapGroup"/>
                </r-panel>
              </v-expansion-panels>
            </v-col>
          </v-tab-item>

          <v-tab-item key="tab_bundles">
            <v-col cols="12">
              <r-smart-offer-bundle-table
                  :items="model.bundles"
                  title="List"
              />
            </v-col>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-card-actions class="resizable-card__actions">
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn
            @click="save"
            color="blue darken-1"
            text
            v-if="!lockedAssess || masterAccess"
        >
          Save
        </v-btn>
      </v-card-actions>

      <div
          class="resize-handle"
          @mousedown.stop.prevent="startResize"
      ></div>
      <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import {ViewRules} from "@/common/ViewRules";
import {SmartOffer} from "@/modules/redactor/models/smart-offer/core/SmartOffer";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";

import RAuthorize from "@/modules/redactor/views/common/r-authorize";
import RTriggerPanel from "@/modules/redactor/views/trigger/r-trigger-panel";
import RGuardTimePanel from "@/modules/redactor/views/guard/r-guard-time-panel";
import RSmartOfferKind from "@/modules/redactor/views/smart-offer/r-smart-offer-kind.vue";
import RSmartOfferBundleTable from "@/modules/redactor/views/smart-offer/r-smart-offer-bundle-table.vue";
import RTalkDropdown from "@/modules/redactor/views/talk/r-talk-dropdown.vue";
import RPanel from "@/modules/redactor/views/utils/r-panel.vue";
import RReminderPanel from "@/modules/redactor/views/reminder/r-reminder-panel.vue";
import RSmartOfferButtonPlace from "@/modules/redactor/views/smart-offer/r-smart-offer-button-place.vue";
import RSmartOfferSkinPanel from "@/modules/redactor/views/smart-offer/reskin/r-smart-offer-skin-panel.vue";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";
import RMapGroupDropdown from "@/modules/redactor/views/map-group/r-map-group-dropdown.vue";

export default {
  name: "r-smart-offer-dialog",
  components: {
    RPreviewDialog,
    RSmartOfferButtonPlace,
    RReminderPanel,
    RPanel,
    RTalkDropdown,
    RSmartOfferBundleTable,
    RSmartOfferKind,
    RGuardTimePanel,
    RTriggerPanel,
    RAuthorize,
    RSmartOfferSkinPanel,
    RMapGroupDropdown
  },
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    tab: null,
    model: new SmartOffer(),
    rules: ViewRules.READONLY,

    dialogWidth: 897,
    dialogHeight: 950,

    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 897,
    resizeStartHeight: 950,
    viewportPadding: 32,
    itemsShowOnLaunch: [
      {name: 'Disabled', value: 0},
      {name: 'Enabled', value: 1},
      {name: 'One Pear Day', value: 2}
    ]
  }),
  computed: {
    lockedAssess() {
      return this.model.locked;
    },
    masterAccess() {
      return RedactorUtils.hasMasterAccess();
    }
  },
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.tab = null;
      this.rules = rules;
      this.model = model || new SmartOffer();

      if (ViewRules.COPY.equal(this.rules)) {
        this.model.locked = false;
      }

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.valid()) {
        this.resolve && this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject && this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },
    onenter() {
      if (!this.lockedAssess || this.masterAccess) {
        this.save();
      } else {
        this.cancel();
      }
    },
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;

      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;

      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;

      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;

      const minWidth = 600;
      const minHeight = 400;

      const maxWidth = window.innerWidth - this.viewportPadding;
      const maxHeight = window.innerHeight - this.viewportPadding;

      if (newWidth < minWidth) newWidth = minWidth;
      if (newWidth > maxWidth) newWidth = maxWidth;

      if (newHeight < minHeight) newHeight = minHeight;
      if (newHeight > maxHeight) newHeight = maxHeight;

      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;

      this.resizing = false;

      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, SmartOffer)
          .then((result) => {
            Object.assign(this.model, result);
          })
          .catch((e) => {
            this.$alert("Failed to parse config!" + e.message);
          });
    }
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}

.resizable-card__body {
  flex: 1;
  overflow: auto;
}

.resizable-card__actions {
  flex-shrink: 0;
}

.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}

.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>